import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Alert, alpha, Switch,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add, Delete, Search, Refresh, CheckCircle, Cancel, Block, Edit,
  Assignment, PersonAdd, PictureAsPdf,
} from '@mui/icons-material';
import axios from 'axios';
import ProductoRapidoDialog from '../components/ProductoRapidoDialog';
import ProductoBuscadorDialog from '../components/ProductoBuscadorDialog';

const URGENCIA_COLOR = { NORMAL: 'default', URGENTE: 'warning', MUY_URGENTE: 'error' };
const URGENCIA_LABEL = { NORMAL: 'Normal', URGENTE: 'Urgente', MUY_URGENTE: 'Muy Urgente' };
const ESTADO_COLOR  = {
  PENDIENTE: 'warning', APROBADO: 'success', RECHAZADO: 'error',
  ANULADO: 'default', PROCESADO: 'info',
};

const initialDetalle = {
  producto_id: '', producto_obj: null,
  descripcion_producto: '', unidad_medida: 'NIU',
  cantidad_solicitada: 1, observaciones: '', stock: null,
};

const initialForm = {
  fecha: new Date().toLocaleDateString('en-CA'),
  area_solicitante: '',
  urgencia: 'NORMAL',
  sucursal_id: '',
  observaciones: '',
  detalles: [{ ...initialDetalle }],
};

function fmt(n) { return String(n || 0).padStart(5, '0'); }

const SectionHeader = ({ children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.5,
    borderLeft: '3px solid', borderColor: 'primary.main',
    pl: 1.5, py: 0.25, mb: 1.5,
    bgcolor: t => alpha(t.palette.primary.main, 0.06),
    borderRadius: '0 4px 4px 0',
  }}>
    <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>{children}</Typography>
  </Box>
);

export default function RequerimientoPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');

  const [open, setOpen]     = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm]     = useState({ ...initialForm });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const [sucursales, setSucursales]   = useState([]);
  const [unidades, setUnidades]       = useState([]);
  const [productOptions, setProductOptions]       = useState({});
  const [productInputValues, setProductInputValues] = useState({});

  const [openNuevoProducto, setOpenNuevoProducto] = useState(false);
  const [productoInputActivo, setProductoInputActivo] = useState({ idx: 0, texto: '' });
  const [openBuscadorProducto, setOpenBuscadorProducto] = useState(false);
  const [buscadorRowIdx, setBuscadorRowIdx]           = useState(0);
  const [copiarNombreADesc, setCopiarNombreADesc]     = useState(true);

  const [rechazarOpen, setRechazarOpen] = useState(false);
  const [rechazarId, setRechazarId]     = useState(null);
  const [motivo, setMotivo]             = useState('');

  // PDF dialog state
  const [openReqPdfDialog, setOpenReqPdfDialog] = useState(false);
  const [reqPdfTarget, setReqPdfTarget] = useState(null);
  const [reqPdfFormatoSeleccionado, setReqPdfFormatoSeleccionado] = useState(() => localStorage.getItem('requerimiento_pdf_formato_default') || 'A4');
  const [guardarReqPdfComoPredeterminado, setGuardarReqPdfComoPredeterminado] = useState(true);
  const [reqPdfResolucion, setReqPdfResolucion] = useState({});
  const [reqPdfResolucionCargando, setReqPdfResolucionCargando] = useState(false);

  const actualizarResolucionFormatoReq = async (nuevoFormato) => {
    if (!reqPdfTarget) return;
    setReqPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-requerimiento/${reqPdfTarget.id}`, { params: { formato: nuevoFormato } });
      setReqPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving REQ PDF template:', err);
    } finally {
      setReqPdfResolucionCargando(false);
    }
  };

  const abrirDialogoPdfReq = async (req) => {
    const formatoGuardado = localStorage.getItem('requerimiento_pdf_formato_default') || 'A4';
    setReqPdfTarget(req);
    setReqPdfFormatoSeleccionado(formatoGuardado);
    setGuardarReqPdfComoPredeterminado(true);
    setReqPdfResolucion({});
    setOpenReqPdfDialog(true);
    setReqPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-requerimiento/${req.id}`, { params: { formato: formatoGuardado } });
      setReqPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving REQ PDF template:', err);
    } finally {
      setReqPdfResolucionCargando(false);
    }
  };

  const cerrarDialogoPdfReq = () => {
    setOpenReqPdfDialog(false);
    setReqPdfTarget(null);
  };

  const descargarPdfReq = () => {
    if (!reqPdfTarget) return;
    const formato = String(reqPdfFormatoSeleccionado || 'A4').toUpperCase();
    if (guardarReqPdfComoPredeterminado) {
      localStorage.setItem('requerimiento_pdf_formato_default', formato);
    }
    const tk = localStorage.getItem('token') || '';
    const query = new URLSearchParams({ formato, mode: 'inline', _ts: Date.now() }).toString();
    window.open(`/api/reporte-pdf/requerimiento/${reqPdfTarget.id}?${query}&token=${tk}`, '_blank');
    cerrarDialogoPdfReq();
  };

  const debouncedFetchRefs = useRef({});

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ─ Catálogos ─────────────────────────────────────────────────────────────

  const fetchSucursales = useCallback(async () => {
    try {
      const r = await axios.get('/api/sucursales/mis-sucursales', { headers });
      const data = r.data || [];
      setSucursales(data);
      return data;
    } catch { return []; }
  }, []);

  const fetchUnidades = useCallback(async () => {
    try {
      const r = await axios.get('/api/unidades', { headers });
      setUnidades(r.data || []);
    } catch { setUnidades([]); }
  }, []);

  const fetchProductosForRow = useCallback((rowIdx, q) => {
    if (!debouncedFetchRefs.current[rowIdx]) {
      debouncedFetchRefs.current[rowIdx] = debounce(async (s) => {
        try {
          const res = await axios.get('/api/productos', { headers, params: { search: s, limit: 30 } });
          setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
        } catch {
          setProductOptions(prev => ({ ...prev, [rowIdx]: [] }));
        }
      }, 300);
    }
    debouncedFetchRefs.current[rowIdx](q);
  }, []);

  const loadInitialProducts = useCallback(async (rowIdx) => {
    try {
      const res = await axios.get('/api/productos', { headers, params: { search: '', limit: 40 } });
      setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
    } catch {}
  }, []);

  // ─ Listado ────────────────────────────────────────────────────────────────

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      if (estadoFiltro) params.estado = estadoFiltro;
      const r = await axios.get('/api/requerimiento', { headers, params });
      setRows(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [search, estadoFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  // ─ Formulario ─────────────────────────────────────────────────────────────

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDetalleChange = (idx, field, value) => {
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => i !== idx ? d : { ...d, [field]: value });
      return { ...prev, detalles };
    });
  };

  const handleProductoSelect = (idx, prod) => {
    if (!prod) return;
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => {
        if (i !== idx) return d;
        return {
          ...d,
          producto_id: prod.id,
          producto_obj: prod,
          descripcion_producto: copiarNombreADesc ? (prod.nombre || '') : (d.descripcion_producto || prod.nombre || ''),
          unidad_medida: prod.unidad_medida || 'NIU',
        };
      });
      return { ...prev, detalles };
    });
    // Cargar stock del producto
    axios.get(`/api/requerimiento/stock/${prod.id}`, { headers })
      .then(r => {
        setForm(prev => {
          const detalles = prev.detalles.map((d, i) => i !== idx ? d : { ...d, stock: r.data.stock });
          return { ...prev, detalles };
        });
      }).catch(() => {});
  };

  const handleBuscadorSelectReq = (prod) => {
    handleProductoSelect(buscadorRowIdx, prod);
    setProductInputValues(prev => ({ ...prev, [buscadorRowIdx]: prod.nombre || '' }));
  };

  const handleAddDetalle = () => {
    setForm(prev => {
      const newIdx = prev.detalles.length;
      loadInitialProducts(newIdx);
      return { ...prev, detalles: [...prev.detalles, { ...initialDetalle }] };
    });
  };

  const handleRemoveDetalle = (idx) => {
    setForm(prev => ({
      ...prev,
      detalles: prev.detalles.length > 1 ? prev.detalles.filter((_, i) => i !== idx) : prev.detalles,
    }));
  };

  // ─ Abrir formulario ───────────────────────────────────────────────────────

  const abrirNuevo = async () => {
    setEditId(null);
    setError('');
    debouncedFetchRefs.current = {};
    setProductOptions({});
    setProductInputValues({});
    const [sucs] = await Promise.all([fetchSucursales(), fetchUnidades()]);
    const autoSucursal = sucs.length === 1 ? sucs[0].id : '';
    setForm({ ...initialForm, sucursal_id: autoSucursal, detalles: [{ ...initialDetalle }] });
    loadInitialProducts(0);
    setOpen(true);
  };

  const abrirEditar = async (id) => {
    try {
      const [rData, sucs] = await Promise.all([
        axios.get(`/api/requerimiento/${id}`, { headers }),
        fetchSucursales(),
      ]);
      await fetchUnidades();
      const d = rData.data;
      debouncedFetchRefs.current = {};
      setProductOptions({});
      const detalles = d.detalles?.length
        ? d.detalles.map(det => ({
            producto_id: det.producto_id || '',
            producto_obj: det.producto_id ? { id: det.producto_id, nombre: det.producto_nombre || det.descripcion_producto, codigo: det.producto_codigo } : null,
            descripcion_producto: det.descripcion_producto || '',
            unidad_medida: det.unidad_medida || 'NIU',
            cantidad_solicitada: det.cantidad_solicitada || 1,
            observaciones: det.observaciones || '',
            stock: null,
          }))
        : [{ ...initialDetalle }];
      const initVals = {};
      detalles.forEach((det, i) => { initVals[i] = det.producto_obj?.nombre || det.descripcion_producto || ''; });
      setProductInputValues(initVals);
      detalles.forEach((_, i) => loadInitialProducts(i));
      setForm({
        fecha: d.fecha?.slice(0, 10) || '',
        area_solicitante: d.area_solicitante || '',
        urgencia: d.urgencia || 'NORMAL',
        sucursal_id: d.sucursal_id || (sucs.length === 1 ? sucs[0].id : ''),
        observaciones: d.observaciones || '',
        detalles,
      });
      setEditId(id);
      setOpen(true);
    } catch { /* */ }
  };

  // ─ Guardar ────────────────────────────────────────────────────────────────

  const guardar = async () => {
    setError('');
    if (!form.fecha) { setError('Ingrese la fecha'); return; }
    const detalles = form.detalles.filter(d => d.descripcion_producto || d.producto_id);
    if (!detalles.length) { setError('Agregue al menos un producto'); return; }
    setSaving(true);
    try {
      const payload = { ...form, detalles };
      if (editId) {
        await axios.put(`/api/requerimiento/${editId}`, payload, { headers });
      } else {
        await axios.post('/api/requerimiento', payload, { headers });
      }
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al guardar');
    }
    setSaving(false);
  };

  // ─ Acciones ───────────────────────────────────────────────────────────────

  const aprobar = async (id) => {
    if (!window.confirm('¿Aprobar este requerimiento?')) return;
    try { await axios.put(`/api/requerimiento/${id}/aprobar`, {}, { headers }); cargar(); } catch { /* */ }
  };

  const abrirRechazar = (id) => { setRechazarId(id); setMotivo(''); setRechazarOpen(true); };
  const confirmarRechazar = async () => {
    try {
      await axios.put(`/api/requerimiento/${rechazarId}/rechazar`, { motivo }, { headers });
      setRechazarOpen(false);
      cargar();
    } catch { /* */ }
  };

  const anular = async (id) => {
    if (!window.confirm('¿Anular este requerimiento?')) return;
    try { await axios.put(`/api/requerimiento/${id}/anular`, {}, { headers }); cargar(); } catch { /* */ }
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Cabecera página */}
      <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1.5, gap: 1, bgcolor: t => alpha(t.palette.primary.main, 0.06), borderRadius: 2 }}>
        <Assignment color="primary" sx={{ fontSize: 28 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} color="primary.dark">Requerimientos de Compra</Typography>
          <Typography variant="caption" color="text.secondary">Gestión de solicitudes internas de materiales</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={abrirNuevo}>Nuevo Requerimiento</Button>
      </Paper>

      {/* Búsqueda */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Buscar área, nro..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ flex: '1 1 220px' }}
        />
        <TextField select label="Estado" size="small" sx={{ minWidth: 150 }}
          value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {['PENDIENTE', 'APROBADO', 'RECHAZADO', 'ANULADO', 'PROCESADO'].map(e => (
            <MenuItem key={e} value={e}>{e}</MenuItem>
          ))}
        </TextField>
        <IconButton onClick={cargar} disabled={loading} color="primary"><Refresh /></IconButton>
      </Paper>

      {/* Tabla listado */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              {['Nro.', 'Fecha', 'Área Solicitante', 'Urgencia', 'Estado', 'Solicitante', 'Acciones'].map(h => (
                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>Cargando...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay requerimientos registrados</TableCell></TableRow>
            ) : rows.map((row, i) => (
              <TableRow key={row.id} hover sx={{ bgcolor: i % 2 === 0 ? 'grey.50' : 'white' }}>
                <TableCell sx={{ fontWeight: 600 }}>REQ-{fmt(row.numero)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.fecha?.slice(0, 10)}</TableCell>
                <TableCell>{row.area_solicitante || '—'}</TableCell>
                <TableCell>
                  <Chip label={URGENCIA_LABEL[row.urgencia] || row.urgencia} color={URGENCIA_COLOR[row.urgencia] || 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={row.estado} color={ESTADO_COLOR[row.estado] || 'default'} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell>{row.usuario_creacion}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {row.estado === 'PENDIENTE' && (
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEditar(row.id)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {row.estado === 'PENDIENTE' && (
                      <>
                        <Tooltip title="Aprobar">
                          <IconButton size="small" color="success" onClick={() => aprobar(row.id)}><CheckCircle fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Rechazar">
                          <IconButton size="small" color="error" onClick={() => abrirRechazar(row.id)}><Cancel fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                    {!['ANULADO', 'RECHAZADO'].includes(row.estado) && (
                      <Tooltip title="Anular">
                        <IconButton size="small" color="warning" onClick={() => anular(row.id)}><Block fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="PDF">
                      <IconButton size="small" color="secondary" onClick={() => abrirDialogoPdfReq(row)}><PictureAsPdf fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Diálogo crear/editar ─────────────────────────────────────────── */}
      <Dialog open={open} onClose={(_, r) => { if (r !== 'backdropClick') setOpen(false); }}
        maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {editId ? `Editar REQ-${fmt(editId)}` : 'Nuevo Requerimiento de Compra'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Solicitud interna de materiales — sin precios</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Sección: Datos del requerimiento */}
          <SectionHeader>Datos del Requerimiento</SectionHeader>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item sx={{ minWidth: '11em' }}>
              <TextField label="Fecha *" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fecha}
                onChange={e => handleFormChange('fecha', e.target.value)} />
            </Grid>
            <Grid item sx={{ minWidth: '20em' }}>
              <TextField label="Área Solicitante" size="small" fullWidth
                value={form.area_solicitante}
                onChange={e => handleFormChange('area_solicitante', e.target.value)} />
            </Grid>
            <Grid item sx={{ minWidth: '13em' }}>
              <TextField select label="Urgencia" size="small" fullWidth
                value={form.urgencia}
                onChange={e => handleFormChange('urgencia', e.target.value)}>
                <MenuItem value="NORMAL">Normal</MenuItem>
                <MenuItem value="URGENTE">Urgente</MenuItem>
                <MenuItem value="MUY_URGENTE">Muy Urgente</MenuItem>
              </TextField>
            </Grid>
            <Grid item sx={{ minWidth: '16em' }}>
              <TextField select label="Sucursal" size="small" fullWidth
                value={form.sucursal_id}
                onChange={e => handleFormChange('sucursal_id', e.target.value)}>
                <MenuItem value="">Sin sucursal</MenuItem>
                {sucursales.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.nombre || `Sucursal ${s.id}`}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item sx={{ minWidth: '28em' }}>
              <TextField label="Observaciones" size="small" fullWidth
                value={form.observaciones}
                onChange={e => handleFormChange('observaciones', e.target.value)} />
            </Grid>
          </Grid>

          {/* Sección: Detalle de productos */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              borderLeft: '3px solid', borderColor: 'primary.main',
              pl: 1.5, py: 0.25, bgcolor: t => alpha(t.palette.primary.main, 0.06),
              borderRadius: '0 4px 4px 0', flexGrow: 1,
            }}>
              <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>Detalle de Productos Requeridos</Typography>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={copiarNombreADesc} onChange={e => setCopiarNombreADesc(e.target.checked)} color="secondary" size="small" />}
              label={<Typography variant="caption">Copiar nombre → descripción</Typography>}
              sx={{ ml: 1 }}
            />
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAddDetalle} sx={{ ml: 1, whiteSpace: 'nowrap' }}>
              Agregar producto
            </Button>
          </Box>

          <TableContainer component={Paper} elevation={0} variant="outlined"
            sx={{ mb: 2, overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 220 }}>Producto</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 100 }}>U.M.</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 80 }}>Stock</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 90 }}>Cantidad</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Observaciones</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 45 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.detalles.map((det, idx) => (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: idx % 2 === 0 ? 'grey.50' : 'white' }}>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={productOptions[idx] || []}
                        filterOptions={x => x}
                        getOptionLabel={o => `${o.codigo ? o.codigo + ' - ' : ''}${o.nombre || ''}`}
                        value={det.producto_obj || null}
                        inputValue={productInputValues[idx] || ''}
                        onOpen={() => { if (!productOptions[idx]?.length) loadInitialProducts(idx); }}
                        onInputChange={(_, v) => {
                          setProductInputValues(prev => ({ ...prev, [idx]: v }));
                          setProductoInputActivo({ idx, texto: v });
                          if (v.length === 0 || v.length >= 2) fetchProductosForRow(idx, v);
                        }}
                        onChange={(_, v) => handleProductoSelect(idx, v)}
                        renderInput={p => <TextField {...p} placeholder="Buscar producto..." />}
                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                        noOptionsText={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>No encontrado</span>
                            <Button size="small" startIcon={<Add />} onClick={() => { setProductoInputActivo({ idx, texto: productInputValues[idx] || '' }); setOpenNuevoProducto(true); }}>
                              Nuevo producto
                            </Button>
                          </Box>
                        }
                        sx={{ minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth value={det.descripcion_producto}
                        onChange={e => handleDetalleChange(idx, 'descripcion_producto', e.target.value)}
                        onKeyDown={e => { if (e.key === 'F1') { e.preventDefault(); setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); } }}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="Buscar producto (F1)">
                              <IconButton size="small" onClick={() => { setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); }} edge="end">
                                <Search fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={unidades}
                        getOptionLabel={u => u.abreviatura || ''}
                        value={unidades.find(u => u.abreviatura === det.unidad_medida) || null}
                        onChange={(_, v) => handleDetalleChange(idx, 'unidad_medida', v?.abreviatura || '')}
                        renderInput={p => <TextField {...p} placeholder="U.M." />}
                        isOptionEqualToValue={(o, v) => o.abreviatura === v?.abreviatura}
                        sx={{ minWidth: 90 }}
                        disableClearable
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2"
                        sx={{ color: det.stock !== null && det.stock <= 0 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                        {det.stock !== null ? parseFloat(det.stock).toFixed(2) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.cantidad_solicitada}
                        onChange={e => handleDetalleChange(idx, 'cantidad_solicitada', parseFloat(e.target.value) || 1)}
                        inputProps={{ min: 0.01, step: 0.01, style: { textAlign: 'right', width: 70 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth value={det.observaciones}
                        onChange={e => handleDetalleChange(idx, 'observaciones', e.target.value)} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemoveDetalle(idx)}
                        disabled={form.detalles.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'grey.50' }}>
          <Button onClick={() => setOpen(false)} startIcon={<Cancel />} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving} startIcon={<Assignment />} size="large">
            {saving ? 'Guardando...' : (editId ? 'Actualizar Requerimiento' : 'Guardar Requerimiento')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Diálogo rechazar ─────────────────────────────────────────────── */}
      <Dialog open={rechazarOpen} onClose={() => setRechazarOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', py: 1.5 }}>Rechazar Requerimiento</DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <TextField label="Motivo de rechazo" fullWidth multiline rows={3} size="small"
            value={motivo} onChange={e => setMotivo(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setRechazarOpen(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarRechazar}>Rechazar</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Format Dialog - Requerimiento */}
      <Dialog open={openReqPdfDialog} onClose={cerrarDialogoPdfReq} maxWidth="sm" fullWidth>
        <DialogTitle>Descargar PDF - Requerimiento de Compra</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Selecciona el formato de impresión. El formato elegido puede quedar como predeterminado.
          </Alert>
          {reqPdfResolucionCargando ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">Resolviendo plantilla...</Typography>
            </Box>
          ) : reqPdfResolucion && reqPdfResolucion.plantilla_nombre ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Plantilla:</strong> {reqPdfResolucion.plantilla_nombre}</Typography>
              <Typography variant="body2"><strong>Ámbito:</strong> {reqPdfResolucion.scope === 'Global' ? 'Global (todas las sucursales)' : reqPdfResolucion.scope === 'Specific' ? 'Específica para esta sucursal' : reqPdfResolucion.scope}</Typography>
              {reqPdfResolucion.is_inherited && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#1976d2' }}>
                  Esta sucursal hereda la asignación de la configuración global
                </Typography>
              )}
            </Alert>
          ) : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
            {[
              { codigo: 'A4', titulo: 'A4', detalle: 'Formato completo' },
              { codigo: 'A5', titulo: 'A5', detalle: 'Formato compacto' },
              { codigo: 'T80', titulo: 'Ticket 80mm', detalle: 'Impresora térmica' }
            ].map((item) => (
              <Paper
                key={item.codigo}
                onClick={() => {
                  setReqPdfFormatoSeleccionado(item.codigo);
                  actualizarResolucionFormatoReq(item.codigo);
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: reqPdfFormatoSeleccionado === item.codigo ? '#1976d2' : '#d0d7de',
                  backgroundColor: reqPdfFormatoSeleccionado === item.codigo ? '#e3f2fd' : '#fff'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{item.titulo}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>{item.detalle}</Typography>
                {localStorage.getItem('requerimiento_pdf_formato_default') === item.codigo && (
                  <Chip label="Predeterminado" color="primary" size="small" sx={{ mt: 1, display: 'flex', mx: 'auto', width: 'fit-content' }} />
                )}
              </Paper>
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={guardarReqPdfComoPredeterminado}
                onChange={(e) => setGuardarReqPdfComoPredeterminado(e.target.checked)}
                color="primary"
              />
            }
            label={`Guardar ${reqPdfFormatoSeleccionado} como formato predeterminado`}
          />
          {reqPdfTarget && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Documento: REQ-{String(reqPdfTarget.numero || reqPdfTarget.id || '').padStart(5, '0')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogoPdfReq}>Cancelar</Button>
          <Button onClick={descargarPdfReq} variant="contained">
            Descargar {reqPdfFormatoSeleccionado}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ProductoRapidoDialog */}
      <ProductoRapidoDialog
        open={openNuevoProducto}
        onClose={() => setOpenNuevoProducto(false)}
        initialNombre={productoInputActivo.texto}
        onProductoCreado={(prod) => {
          const idx = productoInputActivo.idx;
          setProductInputValues(prev => ({ ...prev, [idx]: prod.nombre || '' }));
          setProductOptions(prev => ({ ...prev, [idx]: [prod, ...(prev[idx] || [])] }));
          handleProductoSelect(idx, prod);
          setOpenNuevoProducto(false);
        }}
      />
      <ProductoBuscadorDialog
        open={openBuscadorProducto}
        onClose={() => setOpenBuscadorProducto(false)}
        onSelect={handleBuscadorSelectReq}
      />
    </Box>
  );
}
